// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type MissingCredentialType } from "@src/appV2/redesign/Documents/types";
import {
  INTERVIEW_TYPE,
  type InterviewType,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { type ReactNode } from "react";

import {
  BOOKING_REORDERING_COHORT,
  type BookingReorderingCohort,
  PlacementCandidateScreeningStatus,
} from "../../../../PlacementCandidate/types";
import { getApplyButtonLabel } from "../../../utils";
import { PLACEMENT_VERIFIED_TYPES } from "../../../utils/verified-placement";

export const PLACEMENT_ACTION_TYPE = {
  APPLY: "APPLY",
  BOOK_INTERVIEW: "BOOK_INTERVIEW",
  RESCHEDULE_INTERVIEW: "RESCHEDULE_INTERVIEW",
  COMPLETE_PROFILE: "COMPLETE_PROFILE",
  HAS_CONNECTED_LISTING_INTERVIEW: "HAS_CONNECTED_LISTING_INTERVIEW",
  UPLOAD_LICENSE: "UPLOAD_LICENSE",
  ADD_QUALIFICATION: "ADD_QUALIFICATION",
  UPLOAD_DOCUMENT: "UPLOAD_DOCUMENT",
  UPLOAD_CREDENTIAL: "UPLOAD_CREDENTIAL",
  UPLOAD_MISSING_DOCUMENTS_AND_CREDENTIALS: "UPLOAD_MISSING_DOCUMENTS_AND_CREDENTIALS",
  TALK_TO_RECRUITER: "TALK_TO_RECRUITER",
  APPLY_VIA_WEBLINK: "APPLY_VIA_WEBLINK",
  LATE_CANCEL: "LATE_CANCEL",
  TALK_TO_RECRUITER_NEXT_STEPS: "TALK_TO_RECRUITER_NEXT_STEPS",
  UPDATE_ETA: "UPDATE_ETA",
  VERIFY_ATTENDANCE: "VERIFY_ATTENDANCE",
} as const;

export interface PrimaryActionParams {
  hasApplied: boolean;
  hasInterview: boolean;
  hasInterviewInFuture: boolean;
  hasPendingInterviewInFuture: boolean;
  applicationCompleted: boolean;
  hasInterviewAvailability: boolean;
  isLoadingInterviewAvailability: boolean;
  isProfileComplete: boolean;
  isProfilePending: boolean;
  isProfileInvalid: boolean;
  requirementDisplayName: string;
  requirementSubItemsCount: number;
  hasRequiredLicenses: boolean;
  workplaceState: string;
  hasRequiredDocuments: boolean;
  hasOnlyInvisibleHCPInvalidDocuments: boolean;
  isExemptFromDocumentRequirements: boolean;
  isDocumentRequirementValid?: boolean;
  bookedInterview: boolean;
  pendingInterview: boolean;
  attendedInterview: boolean;
  isProspectWorkplace: boolean;
  requiredInterview: boolean;
  screeningStatus: PlacementCandidateScreeningStatus;
  isRecruiterScreeningEnabled: boolean;
  talkToRecruiterLabel: string;
  talkToRecruiterDescription: string;
  talkToRecruiterUpskillingLabel: string;
  talkToRecruiterUpskillingDescription: string;
  rescheduleRecruiterLabel: string;
  isJobInterviewsEnabled: boolean;
  isJobRequestEnabled: boolean;
  verifiedType?: string;
  isWaitlist: boolean;
  isRestricted: boolean;
  isHiringReferralEnabled: boolean;
  maxReferralBonusAmount: number;
  hasWebApplicationLink: boolean;
  isLoading: boolean;
  isReschedulingPastInterview: boolean;
  startsInWorkplaceToday?: boolean;
  bookingReorderingCohort?: BookingReorderingCohort;
  selfServeInterviewBookingType: InterviewType;
  isChildcareSupportChatEnabled: boolean;
  isSubmitHiringDocumentsLaterEnabled: boolean;
  missingAutoApprovedQualificationName?: string;
  missingAutoApprovedQualificationDisplayLabel?: string;
  missingAutoApprovedQualificationId?: string;
  missingCredentialRequirementType: MissingCredentialType | null;
  hasUnverifiedBookedInterview: boolean;
  hasInProgressInterview: boolean;
  hasWorkerManualEta: boolean;
  isEtaUpdateEnabled: boolean;
  isWorkerDetectedAtWorkplace: boolean;
  hasInitiatedInterviewVerifyAttendance: boolean;
  isEasySchedulingPlacement: boolean;
  hasConnectedListingInterview: boolean;
  connectedListingInterviewStartTime?: string;
  connectedListingHiringManagerName?: string;
  isConnectedListingInterviewInOtherWorkplace?: boolean;
  connectedListingInterviewWorkplaceName?: string;
  /** Whether screen_last cohort can bypass document requirements */
  canBypassDocumentsForScreenLast: boolean;
  isChildCareCenter: boolean;
}

export type PlacementActionType =
  | (typeof PLACEMENT_ACTION_TYPE)[keyof typeof PLACEMENT_ACTION_TYPE]
  | undefined;

export interface PrimaryAction {
  type: PlacementActionType;
  label: string;
  description?: string;
  warning?: string;
  disabled: boolean;
  showCancelInterviewButton: boolean;
  showReferralButton: boolean;
  referralBonusAmount?: number;
  variant?: "contained" | "outlined";
  startIcon?: string;
  additionalContent?: ReactNode;
  isProspectWorkplace?: boolean;
}

function getBookInterviewLabel(
  isProspectWorkplace: boolean,
  hasInterviewAvailability: boolean,
  selfServeInterviewBookingType: InterviewType
): string {
  if (hasInterviewAvailability) {
    if (isProspectWorkplace) {
      return "Book yourself as a walk-in";
    }

    switch (selfServeInterviewBookingType) {
      case INTERVIEW_TYPE.IN_PERSON: {
        return "Book an in-person interview";
      }

      case INTERVIEW_TYPE.PHONE_SCREEN: {
        return "Book phone interview";
      }

      default: {
        return "No interview dates available";
      }
    }
  }

  return "No interview dates available";
}

function getRescheduleInterviewLabel(
  isProspectWorkplace: boolean,
  selfServeInterviewBookingType: InterviewType
): string {
  if (isProspectWorkplace) {
    return "Reschedule walk-in";
  }

  if (selfServeInterviewBookingType === INTERVIEW_TYPE.PHONE_SCREEN) {
    return "Reschedule phone call";
  }

  return "Reschedule interview";
}

/**
 * Pure function that determines the single primary action to display based on priority rules.
 * Uses early returns to guarantee only one action type is returned.
 *
 * Priority order (highest to lowest):
 * 1. Screening - Talk to Recruiter
 * 2. Failed Screening - Upskilling
 * 3. Profile Completion (blocking)
 * 4. Upload License (blocking)
 * 4.5. Add Qualification (auto-approved qualification missing)
 * 5. Upload Document (blocking)
 * 5.5. Update ETA for on going location tracking interviews
 * 6. Reschedule Interview (already booked)
 * 6.5. Talk to Recruiter Next Steps (attended interview)
 * 6.7. Verify attendance (unverified booked interview)
 * 7. Has connected listing interview
 * 8. Book Interview (available)
 * 9. Apply via Weblink (external application)
 * 9. Apply (default action)
 */
export function getPrimaryAction(params: PrimaryActionParams): PrimaryAction {
  const {
    hasApplied,
    hasInterviewInFuture,
    hasPendingInterviewInFuture,
    applicationCompleted,
    hasInterviewAvailability,
    isLoadingInterviewAvailability,
    isProfileComplete,
    isProfilePending,
    isProfileInvalid,
    requirementDisplayName,
    requirementSubItemsCount,
    hasRequiredLicenses,
    workplaceState,
    hasOnlyInvisibleHCPInvalidDocuments,
    isExemptFromDocumentRequirements,
    isDocumentRequirementValid,
    bookedInterview,
    pendingInterview,
    attendedInterview,
    isProspectWorkplace,
    requiredInterview,
    screeningStatus,
    isRecruiterScreeningEnabled,
    talkToRecruiterLabel,
    talkToRecruiterDescription,
    talkToRecruiterUpskillingLabel,
    talkToRecruiterUpskillingDescription,
    rescheduleRecruiterLabel,
    isJobInterviewsEnabled,
    isJobRequestEnabled,
    verifiedType,
    isWaitlist,
    isRestricted,
    isHiringReferralEnabled,
    maxReferralBonusAmount,
    isLoading,
    isReschedulingPastInterview,
    startsInWorkplaceToday,
    bookingReorderingCohort,
    selfServeInterviewBookingType,
    isChildcareSupportChatEnabled,
    isSubmitHiringDocumentsLaterEnabled,
    missingAutoApprovedQualificationName,
    missingAutoApprovedQualificationDisplayLabel,
    missingAutoApprovedQualificationId,
    missingCredentialRequirementType,
    hasInProgressInterview,
    hasWorkerManualEta,
    isEtaUpdateEnabled,
    isWorkerDetectedAtWorkplace,
    hasUnverifiedBookedInterview,
    hasInitiatedInterviewVerifyAttendance,
    isEasySchedulingPlacement,
    hasConnectedListingInterview,
    connectedListingInterviewStartTime,
    connectedListingHiringManagerName,
    isConnectedListingInterviewInOtherWorkplace,
    connectedListingInterviewWorkplaceName,
    canBypassDocumentsForScreenLast,
    isChildCareCenter,
  } = params;

  // Only allow submit docs later bypass for in-person interviews that are NOT easy scheduling
  // and only when an interview booking flow is actually available (requiredInterview)
  // Easy scheduling ("Request to interview"), phone screens, and "Apply to job" flows should still require documents
  const canBypassDocumentsForSubmitLater =
    isSubmitHiringDocumentsLaterEnabled &&
    isChildCareCenter &&
    selfServeInterviewBookingType === INTERVIEW_TYPE.IN_PERSON &&
    !isEasySchedulingPlacement &&
    isJobInterviewsEnabled &&
    requiredInterview;

  const hasPassedScreening = screeningStatus.startsWith(PlacementCandidateScreeningStatus.PASSED);
  const hasFailedScreening = screeningStatus.startsWith(PlacementCandidateScreeningStatus.FAILED);
  const isScreenLastStepCohort = bookingReorderingCohort === BOOKING_REORDERING_COHORT.SCREEN_LAST;

  // For reschedule/cancel logic, treat pending interviews (from submit documents later flow) like booked interviews
  const hasScheduledInterview = bookedInterview || pendingInterview;
  const hasScheduledInterviewInFuture = hasInterviewInFuture || hasPendingInterviewInFuture;

  const showRescheduleInterviewButton =
    hasInterviewAvailability &&
    hasScheduledInterview &&
    isJobInterviewsEnabled &&
    hasRequiredLicenses &&
    (canBypassDocumentsForSubmitLater ||
      canBypassDocumentsForScreenLast ||
      isExemptFromDocumentRequirements ||
      !isDefined(isDocumentRequirementValid) ||
      isDocumentRequirementValid) &&
    !applicationCompleted &&
    (!isReschedulingPastInterview || startsInWorkplaceToday) &&
    !hasUnverifiedBookedInterview;

  const showBookInterviewButton =
    (!hasApplied || !hasScheduledInterview) &&
    !attendedInterview &&
    isJobInterviewsEnabled &&
    requiredInterview &&
    !applicationCompleted &&
    hasRequiredLicenses &&
    (isChildcareSupportChatEnabled ||
      canBypassDocumentsForSubmitLater ||
      canBypassDocumentsForScreenLast ||
      isExemptFromDocumentRequirements ||
      !isDefined(isDocumentRequirementValid) ||
      isDocumentRequirementValid);

  const showHasConnectedListingButton =
    (!hasApplied || !hasScheduledInterview) &&
    !attendedInterview &&
    !applicationCompleted &&
    hasRequiredLicenses &&
    hasConnectedListingInterview &&
    (isChildcareSupportChatEnabled ||
      isSubmitHiringDocumentsLaterEnabled ||
      isExemptFromDocumentRequirements ||
      !isDefined(isDocumentRequirementValid) ||
      isDocumentRequirementValid);

  const showWebApplicationButton =
    verifiedType === PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB && !hasApplied;

  const isPendingScreening = screeningStatus === PlacementCandidateScreeningStatus.PENDING;
  const isBookFirstCohort = bookingReorderingCohort === BOOKING_REORDERING_COHORT.BOOK_FIRST;
  const showScreeningButton =
    !hasPassedScreening &&
    verifiedType !== PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB &&
    isRecruiterScreeningEnabled &&
    !isBookFirstCohort &&
    (!isScreenLastStepCohort ||
      (isScreenLastStepCohort && verifiedType !== PLACEMENT_VERIFIED_TYPES.INSTANT_INTERVIEW) ||
      (isScreenLastStepCohort && hasFailedScreening));

  const showReferralButton = !hasApplied && isHiringReferralEnabled;

  // Priority 1: Screening (if required before applying)
  // For PENDING status, show reschedule screening call even for screen_last cohort
  if (
    (isPendingScreening &&
      isRecruiterScreeningEnabled &&
      verifiedType !== PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB) ||
    (showScreeningButton && !hasFailedScreening)
  ) {
    const label = isPendingScreening ? rescheduleRecruiterLabel : talkToRecruiterLabel;

    return {
      type: PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER,
      label,
      description: talkToRecruiterDescription,
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
    };
  }

  // Priority 2: Failed screening - upskilling
  if (showScreeningButton && hasFailedScreening) {
    return {
      type: PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER,
      label: talkToRecruiterUpskillingLabel,
      description: talkToRecruiterUpskillingDescription,
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
    };
  }

  // Priority 3: Profile completion
  if (!isProfileComplete) {
    const description = `when your ${requirementDisplayName} ${
      requirementSubItemsCount > 1 ? "are" : "is"
    } approved`;

    const profileActionLabel = isProfileInvalid
      ? `Update your ${requirementDisplayName}`
      : `Upload your ${requirementDisplayName}`;

    const fullDescription =
      showRescheduleInterviewButton === true || showBookInterviewButton
        ? `You can book this interview ${description}`
        : `You can apply to this job ${description}`;

    return {
      type: PLACEMENT_ACTION_TYPE.COMPLETE_PROFILE,
      label: isProfilePending ? "Learn more" : profileActionLabel,
      description: fullDescription,
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      variant: isProfilePending ? "outlined" : "contained",
      additionalContent: isProfilePending ? (
        <Stack spacing={2}>
          <Text semibold>
            Your {requirementDisplayName} {requirementSubItemsCount > 1 ? "are" : "is"} in review
          </Text>
          <Text sx={(theme) => ({ color: theme.palette.text.secondary })}>{fullDescription}</Text>
        </Stack>
      ) : undefined,
    };
  }

  // Priority 4: Upload License (blocking)
  if (!hasRequiredLicenses) {
    return {
      type: PLACEMENT_ACTION_TYPE.UPLOAD_LICENSE,
      label: `Upload a valid ${workplaceState} license`,
      warning: `You need a valid ${workplaceState} license to access this job`,
      disabled: false,
      showCancelInterviewButton: false,
      showReferralButton,
      variant: "contained",
    };
  }

  // Priority 4.1: Add auto-approved qualification (blocking)
  if (
    isDefined(missingAutoApprovedQualificationName) &&
    isDefined(missingAutoApprovedQualificationDisplayLabel) &&
    isDefined(missingAutoApprovedQualificationId)
  ) {
    let addQualificationDescription = "to apply to this job";
    if (isWaitlist) {
      addQualificationDescription = "to join the waitlist";
    } else if (requiredInterview) {
      addQualificationDescription = "to schedule an interview";
    } else if (showRescheduleInterviewButton === true || showBookInterviewButton) {
      addQualificationDescription = "to book this interview";
    }

    return {
      type: PLACEMENT_ACTION_TYPE.ADD_QUALIFICATION,
      label: `Add ${missingAutoApprovedQualificationDisplayLabel} role`,
      description: `Add this role ${addQualificationDescription}`,
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      variant: "contained",
    };
  }

  // Priority 4.2: Upload Credential (blocking)
  // Skip this check when submit documents later flow is enabled for in-person interviews - credentials can be submitted later
  // Also skip for screen_last cohort when they haven't been screened yet - credentials can be submitted after screening
  if (
    isDefined(missingCredentialRequirementType) &&
    !canBypassDocumentsForSubmitLater &&
    !canBypassDocumentsForScreenLast
  ) {
    return {
      type: PLACEMENT_ACTION_TYPE.UPLOAD_CREDENTIAL,
      label: `Upload ${
        missingCredentialRequirementType === "BASE_CREDENTIAL" ? "state " : ""
      }qualification`,
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      variant: "contained",
    };
  }

  // Priority 5: Upload Document (blocking)
  // Childcare support chat flow and submit documents later flow (for in-person interviews only) delay the documents requirement check
  // Also skip for screen_last cohort when they haven't been screened yet - documents can be submitted after screening
  if (
    isDefined(isDocumentRequirementValid) &&
    !isDocumentRequirementValid &&
    !isExemptFromDocumentRequirements &&
    !showBookInterviewButton &&
    !isChildcareSupportChatEnabled &&
    !canBypassDocumentsForSubmitLater &&
    !canBypassDocumentsForScreenLast
  ) {
    let uploadLabel = `Upload documents to apply`;
    if (isWaitlist) {
      uploadLabel = `Upload documents to join waitlist`;
    } else if (requiredInterview) {
      uploadLabel = `Upload documents to schedule an interview`;
    }

    return {
      type: PLACEMENT_ACTION_TYPE.UPLOAD_DOCUMENT,
      label: hasOnlyInvisibleHCPInvalidDocuments ? `Documents under review` : uploadLabel,
      warning: hasOnlyInvisibleHCPInvalidDocuments
        ? "Your documents are under review. If you have any questions, contact us at onboarding@clipboardhealth.com"
        : "You are missing required documents for this job",
      disabled: hasOnlyInvisibleHCPInvalidDocuments,
      showCancelInterviewButton: false,
      showReferralButton,
      variant: "contained",
    };
  }

  // Priority 5.5: Update ETA for in-progress interview
  if (hasInProgressInterview && isEtaUpdateEnabled && !isWorkerDetectedAtWorkplace) {
    return {
      type: PLACEMENT_ACTION_TYPE.UPDATE_ETA,
      label: "Update ETA",
      disabled: isLoading || hasWorkerManualEta,
      showCancelInterviewButton: !applicationCompleted,
      showReferralButton: false,
      variant: "contained",
    };
  }

  // Priority 5.75: Upload missing documents for pending SDL interviews
  // When an interview is in PENDING status (from submit documents later flow),
  // the most important action is uploading missing documents, not rescheduling.
  const hasMissingDocuments =
    (isDefined(isDocumentRequirementValid) && !isDocumentRequirementValid) ||
    isDefined(missingCredentialRequirementType);

  if (
    pendingInterview &&
    !bookedInterview &&
    canBypassDocumentsForSubmitLater &&
    hasMissingDocuments &&
    hasScheduledInterviewInFuture &&
    !applicationCompleted &&
    !isExemptFromDocumentRequirements &&
    !canBypassDocumentsForScreenLast
  ) {
    return {
      type: PLACEMENT_ACTION_TYPE.UPLOAD_MISSING_DOCUMENTS_AND_CREDENTIALS,
      label: "Upload missing documents",
      disabled: isLoading,
      showCancelInterviewButton: true,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
    };
  }

  // Priority 6: Reschedule existing interview
  if (showRescheduleInterviewButton) {
    return {
      type: PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW,
      label: getRescheduleInterviewLabel(isProspectWorkplace, selfServeInterviewBookingType),
      disabled: isLoading,
      showCancelInterviewButton: hasScheduledInterviewInFuture && !applicationCompleted,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "outlined",
      isProspectWorkplace,
    };
  }

  // Priority 6.5: Talk to recruiter about next steps (attended interview)
  if (attendedInterview || isWorkerDetectedAtWorkplace) {
    return {
      type: PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER_NEXT_STEPS,
      label: "Need help? Message a recruiter",
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
    };
  }

  // Priority 6.7: Verify attendance (unverified booked interview) it also counts old booked interviews
  if (hasUnverifiedBookedInterview) {
    return {
      type: PLACEMENT_ACTION_TYPE.VERIFY_ATTENDANCE,
      label: "Verify Attendance",
      disabled: isLoading || hasInitiatedInterviewVerifyAttendance,
      showCancelInterviewButton: false,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
    };
  }

  // Priority 7: Has connected listing interview
  if (showHasConnectedListingButton) {
    return {
      type: PLACEMENT_ACTION_TYPE.HAS_CONNECTED_LISTING_INTERVIEW,
      label: "See current interview booking",
      warning:
        isConnectedListingInterviewInOtherWorkplace && isDefined(connectedListingHiringManagerName)
          ? `You already have an interview booked with ${connectedListingHiringManagerName} at ${connectedListingInterviewWorkplaceName} for ${connectedListingInterviewStartTime}.`
          : `You already have an interview booked at ${connectedListingInterviewWorkplaceName} for ${connectedListingInterviewStartTime}.`,
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
      isProspectWorkplace,
    };
  }

  // Priority 8: Book interview (if enabled and available)
  if (showBookInterviewButton) {
    const isBookInterviewLoading = isLoadingInterviewAvailability || isLoading;

    return {
      type: PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
      label: isBookInterviewLoading
        ? "Loading..."
        : getBookInterviewLabel(
            isProspectWorkplace,
            hasInterviewAvailability,
            selfServeInterviewBookingType
          ),
      disabled: isBookInterviewLoading || !hasInterviewAvailability,
      showCancelInterviewButton: false,
      showReferralButton,
      referralBonusAmount: maxReferralBonusAmount,
      variant: "contained",
      isProspectWorkplace,
    };
  }

  // Priority 9: External application
  if (showWebApplicationButton) {
    return {
      type: PLACEMENT_ACTION_TYPE.APPLY_VIA_WEBLINK,
      label: "Apply on web",
      disabled: isLoading,
      showCancelInterviewButton: false,
      showReferralButton,
      variant: "contained",
      startIcon: "arrow-up-right-from-square",
    };
  }

  // Priority 10: Apply (default action)
  return {
    type: PLACEMENT_ACTION_TYPE.APPLY,
    label: getApplyButtonLabel(
      hasApplied,
      applicationCompleted,
      requiredInterview,
      hasScheduledInterview,
      isWaitlist,
      isJobRequestEnabled
    ),
    disabled: hasApplied || isLoading || isRestricted,
    showCancelInterviewButton: hasScheduledInterviewInFuture && !applicationCompleted,
    showReferralButton,
    referralBonusAmount: maxReferralBonusAmount,
    variant: "contained",
  };
}
